import { Box } from "@mui/material";
import {
    DotLegalButton,
    DotLegalEmptyState,
    DotLegalOverflowMenu,
    DotLegalOverflowMenuItem,
    DotLegalPageHeader,
    DotLegalTable,
    DotLegalMenuButton,
    DotLegalTooltip,
    ServerSideConfig,
} from "@dotlegal/dotlegal-ui-components";
import { TableCell } from "@mui/material";
import TableRow from "@common/components/tableRow/TableRow";
import { useTranslation } from "@localization/useTranslation";
import { ILegalEntitiesProps, useLegalEntitiesHook } from "./LegalEntities.hooks";
import { LegalEntityFilterItem } from "./LegalEntities.types";
import { LegalEntityColumn, LegalEntityColumnId } from "./legalEntityColumns";
import DynamicCellRenderer from "@common/components/dynamicCellRenderer/DynamicCellRenderer";
import { EntityType } from "@common/Common.types";
import NoData from "../legalEntityTable/noData.svg?react";
import { WhiteLockIcon } from "@common/icons";
import PlanDialogButtonWrapper from "../../plan/planDialogWrapper/planDialogWrapper";
import DotLegalDeleteDialogWrapper from "@common/components/dotLegalDeleteDialog/DotLegalDeleteDialogWrapper.tsx";
import DeleteLegalEntityBlockedDialog from "../deleteLegalEntityBlockedDialog/DeleteLegalEntityBlockedDialog.tsx";
import LegalEntityAuditClassificationReportDialog from "../legalEntityAuditClassificationReport/legalEntityAuditClassificationReportDialog/LegalEntityAuditClassificationReportDialog.tsx";
import { useDeleteLegalEntity } from "../DeleteLegalEntity.hooks.ts";
import FilterBar from "@common/components/filterBar/FilterBar";
import ListPageToolbar from "@common/components/listPageToolbar/ListPageToolbar";
import SavedViewsMenu from "@common/components/savedViews/SavedViewsMenu";
import ExcelExportButton from "@common/components/excelExportButton/ExcelExportButton";
import TableDisplayFields from "@common/components/tableDisplayFields/TableDisplayFields";
import { LegalEntityFieldGroupingOrder } from "@common/components/filterPopover/FilterPopover.types";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

// The tint the app already uses to mark the one designated row in a table - the main contract document, the main
// policy document - reused here for the group company new entities are created against.
const preselectedRowBackgroundColor = "#fafaff";

function LegalEntities(props: ILegalEntitiesProps) {
    const { translateString } = useTranslation();
    const {
        isGroupEntities,
        filterPage,
        isLoading,
        isLoadingFilters,
        isLoadingPage,
        legalEntityFilterOptions,
        headers,
        tableFields,
        hasData,
        isSearch,
        emptyStateText,
        rows,
        totalCount,
        totalPages,
        pageNumber,
        setPageNumber,
        pageSize,
        sortByColumnId,
        sortDirection,
        onSortChange,
        searchText,
        onSearchChange,
        breadCrumbs,
        selectedColumns,
        filterData,
        setDisplayData,
        onFilterBarUpdate,
        clearFilterData,
        hasFilterChanged,
        hasDisplayChanged,
        isSavedView,
        pinnedFilterIds,
        setPinnedFilterIds,
        savedView,
        currentViewId,
        onViewSaved,
        onViewDeleted,
        onChangeView,
        onDownloadExcelReportClick,
        getLegalEntityOverviewUrl,
        getLegalEntityEditUrl,
        legalEntityScope,
        onGoToLegalEntityClick,
        isPreselectedGroupEntity,
        openCreateLegalEntity,
        reportMenuItems,
        openReportType,
        closeReportDialog,
        refetch,
        permissions,
        addonsFeatures,
    } = useLegalEntitiesHook(props);

    const { legalEntityToDelete, setLegalEntityToDelete, deleteLegalEntity, isDeletingLegalEntity, blockedDeletion, closeBlockedDeletion } =
        useDeleteLegalEntity(refetch);

    const canCreate = isGroupEntities ? permissions.canManageGroupEntities : permissions.companyPermissions.create;
    const canEdit = isGroupEntities ? permissions.canManageGroupEntities : permissions.companyPermissions.edit;
    const canDelete = isGroupEntities ? permissions.canManageGroupEntities : permissions.companyPermissions.delete;
    const canRead = isGroupEntities ? permissions.canManageGroupEntities : permissions.companyPermissions.read;
    const disableCreateButton = !addonsFeatures.organizationalManagement && isGroupEntities;

    function getMenuItems(row: LegalEntityFilterItem) {
        const menuItems: Array<DotLegalOverflowMenuItem> = [];

        if (canEdit) {
            menuItems.push({ menuItem: translateString("edit"), href: getLegalEntityEditUrl(row) });
        }

        if (canDelete) {
            menuItems.push({
                menuItem: translateString("delete"),
                onClick: () => setLegalEntityToDelete({ legalEntityId: row.id, name: row.name }),
            });
        }

        return menuItems;
    }

    const serverSideConfig: ServerSideConfig = {
        order: sortDirection === 1 ? "desc" : "asc",
        orderBy: sortByColumnId,
        onSortChange: onSortChange,
        page: pageNumber,
        from: totalCount > 0 ? (pageNumber - 1) * pageSize + 1 : 0,
        to: Math.min(pageNumber * pageSize, totalCount),
        totalPages: totalPages,
        totalCount: totalCount,
        onPageChange: setPageNumber,
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
            <DotLegalPageHeader
                breadCrumbs={breadCrumbs}
                rightComponent={
                    canCreate ? (
                        <Box sx={{ display: "flex", gap: 1 }}>
                            {reportMenuItems.length > 0 && (
                                <DotLegalMenuButton buttonType={"secondary"} size="medium" menuButtonSection={{ menuItems: reportMenuItems }}>
                                    {translateString("reports")}
                                </DotLegalMenuButton>
                            )}
                            <PlanDialogButtonWrapper text={translateString("upgradeToActivateAddOn")} enabled={disableCreateButton}>
                                <DotLegalButton
                                    buttonType="primary"
                                    size="medium"
                                    onClick={openCreateLegalEntity}
                                    disabled={disableCreateButton}
                                    endIcon={disableCreateButton ? WhiteLockIcon : undefined}
                                    toolTip={disableCreateButton ? translateString("upgradeToActivateAddOn") : undefined}
                                >
                                    {translateString("createLegalEntity")}
                                </DotLegalButton>
                            </PlanDialogButtonWrapper>
                        </Box>
                    ) : undefined
                }
            />

            <TablePagePaper>
                {(hasData || isSearch) && (
                    <ListPageToolbar
                        savedViewsMenu={
                            <SavedViewsMenu
                                currentViewId={currentViewId}
                                parentIsLoading={isLoadingPage}
                                currentFilterData={filterData}
                                currentDisplayData={selectedColumns}
                                onViewChange={onChangeView}
                                onViewSaved={onViewSaved}
                                onViewDeleted={onViewDeleted}
                                filterPage={filterPage}
                                hasFilterOrDisplayDataChanged={hasFilterChanged || hasDisplayChanged}
                            />
                        }
                        leftContent={
                            <FilterBar
                                fetchFilterData={!isLoadingFilters}
                                isLoading={isLoadingPage}
                                filterData={legalEntityFilterOptions}
                                onMenuClose={onFilterBarUpdate}
                                onRemove={onFilterBarUpdate}
                                pinnedFilterIds={pinnedFilterIds}
                                onPinnedFilterIdsChange={setPinnedFilterIds}
                                onClearFilter={hasFilterChanged ? () => clearFilterData() : undefined}
                                clearFilterText={hasFilterChanged ? (isSavedView ? "savedView" : "defaultView") : undefined}
                                groupingOrder={LegalEntityFieldGroupingOrder}
                                searchValue={searchText}
                                searchLabel={translateString("search")}
                                onSearchChange={(value) => onSearchChange(value ?? "")}
                            />
                        }
                        rightContent={
                            <>
                                {savedView && canRead && (
                                    <TableDisplayFields
                                        selectedFields={selectedColumns}
                                        onSave={async (columns) => {
                                            setDisplayData(columns);
                                        }}
                                        options={tableFields}
                                        hasDisplayChanged={hasDisplayChanged}
                                        isCustomView={isSavedView}
                                        savedViewFields={savedView.displayData}
                                        groupingOrder={LegalEntityFieldGroupingOrder}
                                    />
                                )}
                                {savedView && <ExcelExportButton onClick={onDownloadExcelReportClick} isDisabled={isLoadingPage} />}
                            </>
                        }
                    />
                )}

                {hasData ? (
                    <DotLegalTable
                        hasOverflowMenu={canEdit || canDelete}
                        internalScroll
                        headers={headers}
                        // Inert: sorting is server side, so the header arrow follows serverSideConfig.orderBy. The
                        // prop is typed against the row model, which carries no user friendly id.
                        defaultOrderBy={LegalEntityColumnId.name}
                        defaultOrder={"asc"}
                        data={rows}
                        isLoading={isLoading}
                        disableFilterAndSortAndSlicing
                        serverSide={serverSideConfig}
                        rowsPerPage={pageSize}
                        clickableRows
                        paginationLabelOf={translateString("labelOf")}
                        emptyText={emptyStateText}
                        renderRow={(row) => (
                            <DotLegalTooltip
                                key={row.id}
                                text={isPreselectedGroupEntity(row) ? translateString("preselectedOnCreate") : undefined}
                                position={"bottom"}
                            >
                                <TableRow
                                    onClick={() => onGoToLegalEntityClick(row)}
                                    inactive={!row.isActive}
                                    inactiveTooltip={translateString("legalEntityDeactivated")}
                                    sx={{ cursor: "pointer", backgroundColor: isPreselectedGroupEntity(row) ? preselectedRowBackgroundColor : undefined }}
                                >
                                    {row.fields.map((field) => (
                                        <DynamicCellRenderer
                                            key={`${row.id}-${field.customFieldId ?? field.column}`}
                                            field={field}
                                            entityId={row.id}
                                            riskEntityType={EntityType.LegalEntity}
                                            legalEntityScope={legalEntityScope}
                                            isIdentifier={field.column === LegalEntityColumn.UserFriendlyId}
                                            linkTo={field.column === LegalEntityColumn.Name ? getLegalEntityOverviewUrl(row) : undefined}
                                        />
                                    ))}
                                    <TableCell align={"right"} onClick={(event) => event.stopPropagation()}>
                                        <DotLegalOverflowMenu items={getMenuItems(row)} />
                                    </TableCell>
                                </TableRow>
                            </DotLegalTooltip>
                        )}
                    />
                ) : (
                    <DotLegalEmptyState icon={<NoData />} text={emptyStateText} />
                )}
            </TablePagePaper>

            {legalEntityToDelete && (
                <DotLegalDeleteDialogWrapper
                    open
                    itemForDeletion={legalEntityToDelete.name}
                    onDialogClose={() => setLegalEntityToDelete(undefined)}
                    onOkClick={() => deleteLegalEntity(legalEntityToDelete)}
                    inProgress={isDeletingLegalEntity}
                />
            )}

            {openReportType && <LegalEntityAuditClassificationReportDialog type={openReportType} scope={props.scope} onClose={closeReportDialog} />}

            {blockedDeletion && (
                <DeleteLegalEntityBlockedDialog
                    legalEntityName={legalEntityToDelete?.name ?? ""}
                    blockers={blockedDeletion.blockers}
                    onDialogClose={closeBlockedDeletion}
                />
            )}
        </Box>
    );
}

export default LegalEntities;
